import React, { useEffect } from 'react';
import MultipleLinesTextBubble from '@gp/components/MultipleLinesTextBubble';
import PercentTendencyFloat from '@gp/components/PercentTendencyFloat';
import TriggerFoldBtn from '@gp/components/TriggerFoldBtn';
import InfoBubble from '@gp/components/InfoBubble';
import ProjectItem from '@gp/components/ProjectItem';
import TextAreaMore from '@gp/components/TextAreaMore';
import EllipsisTextBubble from '@gp/components/EllipsisTextBubble';
import LinkJump from '@gp/components/LinkJump';
import { history } from 'umi';
import { MENU_ID_MAP, SLASH_G_POP } from '@gp/constants/const';
import { TABLE_COMMON_COL_HEADER_MAP, DATA_SOURCE_MAP } from './constants';
import { getFormatNumPlus, getNumberOfDotUnit, queryJumpable } from '@gp/utils/common';
import { FIELDS1, FIELD_NAMES } from '@gp/utils/metricTips';

import type { IRecord } from '@gp/types/global';

// 公共列表体每项加下边框效果，只有第一行有这种效果
// const addBorderBottomLine = (e: any) => {
//   return (
//     <>
//       {e}
//       <span className="border-bottom-line"></span>
//     </>
//   );
// };

// 公共列表体
const commonCol = (value: string, percent: number) => {
  // if (!value && !percent && String(value) !== '0' && String(percent) !== '0') return '-';

  return (
    <div className="dbg-table-metrics">
      <span className="dbg-table-metrics-value">{getFormatNumPlus(value || '')}</span>
      <PercentTendencyFloat className="dbg-table-metrics-precent" value={percent} />
    </div>
  );
};

const toDetail = (studioId: string, studioName: string, isJump = true) => {
  if (!studioId) return '';
  const filter = encodeURIComponent(
    JSON.stringify({
      studioId,
      studioName,
      menuFrom: MENU_ID_MAP['studio-dashboard_studio-overall'], // 用于标识左侧菜单栏当前选中高亮
      tab: 'projects',
    }),
  );
  const url = `${SLASH_G_POP}/Studio/PortfolioOverview/Detail?filter=${filter}`;
  if (isJump) {
    history.push(url);
  }

  return url;
};

const METRIC_KEYS = [
  'total_revenue',
  'total_revenue_target',
  'ytm_total_revenue',
  'ytm_total_revenue_target',
  'total_mau',
];

const METRIC_TIPS_CFG_MAPPING: IRecord = {
  total_revenue: {
    definition: `
      Actual total sales amount.
      <br />
      Noted: This amount does not deduct steam/Epic platform share, and also include the sales amount of retail activation code (e.g. Humble Bundle, Heybox, etc.).
    `,
    source: 'Studio Reporting Package',
    updateCadence: 'Monthly',
  },
  total_revenue_target: {
    definition: `
      Total sales amount annual target.
      <br />
      Noted: This amount does not deduct steam/Epic platform share, and also include the sales amount of retail activation code (e.g. Humble Bundle, Heybox, etc.).
    `,
    source: 'Studio Budget',
    updateCadence: 'Annually',
  },
  ytm_total_revenue: {
    definition: `
      Total sales amount to the month.
      <br />
      Noted: This amount does not deduct steam/Epic platform share, and also include the sales amount of retail activation code (e.g. Humble Bundle, Heybox, etc.).
    `,
    source: 'Studio Budget',
    updateCadence: 'Monthly',
  },
  ytm_total_revenue_target: {
    definition: `
      Total sales amount target to the month.
      <br />
      Noted: This amount does not deduct steam/Epic platform share, and also include the sales amount of retail activation code (e.g. Humble Bundle, Heybox, etc.).
    `,
    source: 'Studio Budget',
    updateCadence: 'Monthly',
  },
  total_mau: {
    definition: `
      Number of users who have logged in to the game during the statistical period (userid deduplicated).
      <br />
      Time zone: Steam sales data is in UTC-8 time zone, Epic/XBOX/PlayStation sales data is in UTC+0 time zone.
    `,
    source: 'Studio Operational Report',
    updateCadence: 'Monthly',
  },
};

// 获取列配置
const getColumns = (
  isHideExpandButton: boolean,
  unFoldList: string[],
  setUnFoldList: React.Dispatch<React.SetStateAction<string[]>>,
  count: number,
  date: string,
  triggerFoldExpand: boolean,
  toGameDetail,
  isNeedData = false,
) => {
  // useEffect(() => {
  //   TABLE_COMMON_COL_HEADER_MAP.map((t: CommonColHeaderItemType) => {
  //     // 判断展开列表中是否存在，存在的话，删除展开列表中的key，不存在的话，把key加到展开项中
  //     const index = unFoldList?.findIndex((item: string) => item === t.keyName);
  //     // 展开最后一项时，往右滚动到最右边，让用户知道后面还有展开的列，增强用户体验
  //     if (index === -1 && t?.keyName === 'total_mau') {
  //       const t = setTimeout(() => {
  //         const tableBodyEl: any = document.querySelector(
  //           '.dbg-studio-overall-projects-live-breakdown .dbg-breakdown-list-table .tea-table__body',
  //         );
  //         if (tableBodyEl) {
  //           const currentWidth = tableBodyEl.offsetWidth,
  //             scrollLeft = tableBodyEl.scrollLeft,
  //             scrollWidth = tableBodyEl.scrollWidth;
  //           // 出现滚动条时，此时自动向右滚动到最右边
  //           if (scrollWidth - currentWidth > 0) {
  //             tableBodyEl.scrollLeft = scrollWidth - currentWidth;
  //           }
  //         }
  //         clearTimeout(t);
  //       }, 200);
  //     }
  //   });
  // }, [triggerFoldExpand]);

  // 组织公共列配置
  const orgCol = TABLE_COMMON_COL_HEADER_MAP.map((t: CommonColHeaderItemType) => {
    const headers: [string, string] = t.title.split('\n') as [string, string];
    const headerFirTxt = headers[0]?.replace('<time>', date || '-');
    const headerSecTxt = headers[1];
    // 展开项中存在或者拥有子类时，正常宽度显示，否则宽度为0不展示
    const width = !triggerFoldExpand
      ? unFoldList?.includes(t.groupKey) || t.isGroup
        ? t.width
        : 0
      : t.width;

    return {
      key: t.keyName,
      header: (
        <div
          className={`dbg-table-header-flex ${width == 0 ? 'dbg-table-header-flex--nowrap' : ''}`}
        >
          <div className="dbg-table-header-icon">
            {t.dataSource && (
              <InfoBubble
                className="dbg-table-header-datasource-bubble"
                DomOrText={
                  <img width={20} height={20} src={DATA_SOURCE_MAP.svg[t.dataSource]} alt="" />
                }
                customBubbleContent={
                  <div className="dbg-table-header-datasource-bubble-inner">
                    Data of {DATA_SOURCE_MAP.metric[t.metric]} comes from &nbsp;
                    <img
                      width={18}
                      height={18}
                      src={DATA_SOURCE_MAP.bubblesvg[t.dataSource]}
                      alt=""
                    />
                    &nbsp;
                    {DATA_SOURCE_MAP.name[t.dataSource]}.
                  </div>
                }
              />
            )}
          </div>
          {!METRIC_KEYS.includes(t?.keyName) ? (
            <div className="dbg-table-header-text">
              {headerFirTxt}
              <br />
              {headerSecTxt}
            </div>
          ) : (
            <MultipleLinesTextBubble
              fields={FIELDS1}
              names={FIELD_NAMES}
              data={{
                definition: METRIC_TIPS_CFG_MAPPING[t?.keyName]?.definition,
                source: METRIC_TIPS_CFG_MAPPING[t?.keyName]?.source,
                updateCadence: METRIC_TIPS_CFG_MAPPING[t?.keyName]?.updateCadence,
              }}
            >
              <div className="dbg-table-header-text">
                {headerFirTxt}
                <br />
                {headerSecTxt}
              </div>
            </MultipleLinesTextBubble>
          )}
          {
            // !isHideExpandButton && !!t.isGroup && (
            // <div
            //   className="header-fold-btn"
            //   style={{
            //     zIndex:
            //       t.keyName === 'total_mau' &&
            //       !unFoldList?.includes(
            //         'total_mau',
            //       ) /* 展开项中不包含total_mau */
            //         ? 0
            //         : 10,
            //   }}
            //   onClick={(e: any) => {
            //     e.stopPropagation();
            //   }}
            // >
            //   <TriggerFoldBtn
            //     foldBubble="Expand by platform"
            //     state={unFoldList?.includes(t?.keyName)}
            //     onClick={() => {
            //       // 判断展开列表中是否存在，存在的话，删除展开列表中的key，不存在的话，把key加到展开项中
            //       const index = unFoldList?.findIndex(
            //         (item: string) => item === t.keyName,
            //       );
            //       const newUnFoldList =
            //         index >= 0
            //           ? unFoldList?.filter(
            //               (item: string) => item !== t.keyName,
            //             )
            //           : [...unFoldList, t.keyName];
            //       setUnFoldList(newUnFoldList);
            //       // 展开最后一项时，往右滚动到最右边，让用户知道后面还有展开的列，增强用户体验
            //       if (index === -1 && t?.keyName === 'total_mau') {
            //         const t = setTimeout(() => {
            //           const tableBodyEl: any = document.querySelector(
            //             '.dbg-studio-overall-projects-live-breakdown .dbg-breakdown-list-table .tea-table__body',
            //           );
            //           if (tableBodyEl) {
            //             const currentWidth = tableBodyEl.offsetWidth,
            //               scrollLeft = tableBodyEl.scrollLeft,
            //               scrollWidth = tableBodyEl.scrollWidth;
            //             // 出现滚动条时，此时自动向右滚动到最右边
            //             if (scrollWidth - currentWidth > 0) {
            //               tableBodyEl.scrollLeft = scrollWidth - currentWidth;
            //             }
            //           }
            //           clearTimeout(t);
            //         }, 200);
            //       }
            //     }}
            //   />
            // </div>
            // )
          }
        </div>
      ),
      align: 'right',
      width,
      render: (cvm: any) => {
        return width ? <>{commonCol(cvm?.[t.keyName], cvm?.[`${t.keyName}_ratio`])}</> : null;
      },
    };
  });

  return [
    {
      key: 'projects',
      header: <span>Projects ({getNumberOfDotUnit(count)})</span>,
      align: 'left',
      fixed: 'left',
      width: 240,
      render: (cvm: any) => {
        return (
          <div className="new-td">
            {['All Games', 'All Games']?.includes(cvm?.game_name) ? (
              'All Games'
            ) : (
              <ProjectItem
                src={cvm?.game_cover || ''}
                title={cvm?.game_name || '-'}
                platform={cvm?.platform || ''}
                platformPlaceHolder="-"
                toDetails={toGameDetail}
                data={isNeedData ? cvm : null}
              />
            )}
          </div>
        );
      },
    },
    {
      key: 'studio',
      header: <span>Studio</span>,
      align: 'left',
      // fixed: 'left',
      width: 180,
      render: (cvm: any) => {
        return (
          <div className="new-td">
            <LinkJump
              // style={{
              //   textDecoration: 'none',
              //   color: '#222',
              //   display: 'flex',
              //   width: '100%',
              // }}
              className="dbg-table-common-text"
              url={toDetail(cvm?.studio_id, cvm?.studio, false)}
            >
              <span
                className="dbg-table-studio-name gp-cursor-pointer"
                onClick={(e) => {
                  if (!queryJumpable(e)) return;
                  toDetail(cvm?.studio_id, cvm?.studio);
                }}
              >
                <EllipsisTextBubble text={cvm?.studio || '-'} />
              </span>
            </LinkJump>
          </div>
        );
      },
    },
    {
      key: 'bo',
      header: <span>BO</span>,
      align: 'left',
      width: 120,
      render: (cvm: any) => {
        let businessOwner = cvm.bo || '';
        try {
          businessOwner = Object.values(JSON.parse(cvm.bo))[0] || businessOwner;
        } catch (error) {
          // console.warn(error);
        }

        return (
          <div className="new-td gp-word-break-word" title={businessOwner || '-'}>
            {businessOwner || '-'}
          </div>
        );
      },
    },
    {
      key: 'genre',
      header: <span>Genre</span>,
      align: 'left',
      width: 140,
      render: (cvm: any) => {
        return (
          <div className="new-td">
            <InfoBubble
              className="globalpedia-modal-font14"
              customBubbleContent={
                cvm?.genre || cvm?.sub_genre ? (
                  <>
                    <div className="bubble-genre">
                      Genre: {cvm?.genre?.replaceAll('|', ', ') || '-'}
                    </div>
                    <div className="bubble-sub-genre">
                      Sub Genre: {cvm?.sub_genre?.replaceAll('|', ', ') || '-'}
                    </div>
                  </>
                ) : undefined
              }
              DomOrText={
                <TextAreaMore
                  className="gp-word-break-word"
                  type="ellipsis"
                  isMustNoButton
                  text={cvm?.genre?.replaceAll('|', ', ') || '-'}
                  ellipsisLineClamp={2}
                />
              }
            />
          </div>
        );
      },
    },
    ...orgCol,
  ];
};

export { getColumns };
